import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sparkles, RefreshCw, Brain, AlertCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { API } from '../utils/api';

// Lightweight markdown renderer for the briefing (headers + bold + bullets).
function renderBriefing(text) {
  const lines = (text || '').split('\n');
  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} className="h-2" />;

    if (trimmed.startsWith('### ')) {
      return (
        <h4 key={i} className="text-cyan-300 font-bold text-sm mt-4 mb-1.5 font-['Manrope'] tracking-wide">
          {trimmed.replace(/^###\s+/, '')}
        </h4>
      );
    }

    const bulletMatch = trimmed.match(/^[-*]\s+(.*)$/);
    const content = bulletMatch ? bulletMatch[1] : trimmed;
    const parts = content.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith('**') && p.endsWith('**')
        ? <strong key={j} className="text-white font-semibold">{p.slice(2, -2)}</strong>
        : <span key={j}>{p}</span>
    );

    if (bulletMatch) {
      return (
        <div key={i} className="flex gap-2 text-sm text-slate-300 leading-relaxed mb-1">
          <span className="text-cyan-500 mt-0.5">▸</span>
          <span>{parts}</span>
        </div>
      );
    }
    return <p key={i} className="text-sm text-slate-300 leading-relaxed mb-1">{parts}</p>;
  });
}

export default function AIMarketAnalyst() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchAnalysis = useCallback(async (refresh = false) => {
    if (refresh) setRefreshing(true); else setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/ai/market-analysis${refresh ? '?refresh=true' : ''}`, { timeout: 60000 });
      setData(res.data);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const streamAnalysis = useCallback(async () => {
    setRefreshing(true);
    setError(false);
    let meta = {};
    let full = '';
    try {
      const res = await fetch(`${API}/ai/market-analysis/stream`, { method: 'GET' });
      if (!res.ok || !res.body) throw new Error('stream failed');
      // clear current text so the briefing types in live
      setData(prev => ({ ...(prev || {}), analysis: '' }));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          let evt;
          try { evt = JSON.parse(jsonStr); } catch (e) { continue; }
          if (evt.meta) meta = evt.meta;
          if (evt.delta) {
            full += evt.delta;
            setData(prev => ({ ...(prev || {}), ...meta, analysis: full }));
          }
          if (evt.error) throw new Error('llm error');
        }
      }
      if (!full) throw new Error('empty');
    } catch (e) {
      setError(true);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchAnalysis(false); }, [fetchAnalysis]);

  const generatedTime = data?.generated_at
    ? new Date(data.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <Card
      className="relative overflow-hidden bg-gradient-to-br from-[#0D131C] via-[#0B1220] to-[#0A0E14] border-[#1E293B]"
      data-testid="ai-market-analyst"
    >
      {/* accent glow */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 w-64 h-64 bg-blue-600/10 rounded-full blur-3xl" />

      <div className="relative p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center">
              <Brain className="w-6 h-6 text-cyan-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="font-bold text-white font-['Manrope'] text-lg">AI Market Analyst</h2>
                <span className="px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-[10px] font-mono text-cyan-300 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Claude
                </span>
              </div>
              <p className="text-xs text-slate-500 font-mono">
                {generatedTime ? `Live briefing · updated ${generatedTime}` : 'Real-time market intelligence'}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={streamAnalysis}
            disabled={loading || refreshing}
            className="text-slate-400 hover:text-cyan-300 hover:bg-cyan-500/10"
            data-testid="analyst-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="space-y-3 animate-pulse" data-testid="analyst-loading">
            <div className="h-3 bg-[#1E293B] rounded w-1/3" />
            <div className="h-3 bg-[#1E293B] rounded w-full" />
            <div className="h-3 bg-[#1E293B] rounded w-11/12" />
            <div className="h-3 bg-[#1E293B] rounded w-1/4 mt-4" />
            <div className="h-3 bg-[#1E293B] rounded w-full" />
            <div className="h-3 bg-[#1E293B] rounded w-4/5" />
            <p className="text-xs text-cyan-400/70 font-mono pt-2">Claude is analyzing the markets…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="analyst-error">
            <AlertCircle className="w-8 h-8 text-[#FF3B30] mb-2" />
            <p className="text-slate-400 text-sm mb-3">Couldn't generate the market briefing right now.</p>
            <Button size="sm" variant="outline" onClick={() => fetchAnalysis(true)} className="border-[#1E293B] text-cyan-300">
              Try again
            </Button>
          </div>
        ) : (
          <div data-testid="analyst-content">
            {renderBriefing(data?.analysis)}
            <p className="text-[10px] text-slate-600 mt-4 pt-3 border-t border-[#1E293B]">
              AI-generated analysis for educational purposes only. Not financial advice.
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}
